var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Typography } from '@mui/material';
import { mean, round, times } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlanTypeCategory } from '../../../enum';
import { FormField, NativeNumberInput, TableBody, TableCell, TableRow } from '../../common';
import { ExpandableRowLabel, TableHierarchyCell } from '../../common/tableHierarchy';
import { SummaryCell } from '../common/baseTable';
import { STICKIES } from './constants';
import { ExtraColumnCells, ExtraColumnEmptyCells } from './extraColumns';
import { PeriodCells } from './periodCells';
import { StyledCountryRow, StyledInputTableCell, StyledLocationTypeRow, StyledSiteRow, StyledTotalRow } from './styles';
import { formatValueWithSuffix } from './utils';
export var GlobalLocationRows = function (_a) {
  var name = _a.name,
    min = _a.min,
    max = _a.max,
    decimal = _a.decimal,
    suffix = _a.suffix,
    disabled = _a.disabled,
    defaultValue = _a.defaultValue,
    extraColumns = _a.extraColumns,
    sharedPeriodProps = _a.sharedPeriodProps,
    border = _a.border,
    summaryGlobal = _a.summaryGlobal,
    hideSticky = _a.hideSticky;
  return _jsx(TableBody, {
    children: _jsxs(TableRow, {
      children: [!hideSticky && _jsx(TableCell, {
        stickySizes: STICKIES,
        component: "th"
      }), !!extraColumns.length && _jsx(ExtraColumnCells, {
        extraColumns: extraColumns,
        fieldPrefix: ""
      }), _jsx(PeriodCells, __assign({}, sharedPeriodProps, {
        renderCell: function (periodIdx, year, month) {
          return _jsx(StyledInputTableCell, __assign({
            padding: "none",
            highlightBorder: border(month, year)
          }, {
            children: _jsx(FormField, __assign({
              disableHelperText: true,
              required: true,
              name: "volumes.".concat(periodIdx, ".").concat(name),
              defaultValue: defaultValue
            }, {
              children: _jsx(NativeNumberInput, {
                min: min,
                max: max,
                decimal: decimal,
                disabled: disabled,
                suffix: suffix
              })
            }))
          }));
        },
        renderTotalCell: function (year) {
          var _a;
          return _jsx(SummaryCell, {
            padding: "none",
            greyColor: true,
            value: (_a = summaryGlobal[year]) === null || _a === void 0 ? void 0 : _a.total,
            decimal: decimal,
            suffix: suffix
          });
        },
        renderAverageCell: function (year) {
          var _a;
          return _jsx(SummaryCell, {
            padding: "none",
            greyColor: true,
            value: (_a = summaryGlobal[year]) === null || _a === void 0 ? void 0 : _a.avg,
            decimal: decimal,
            highlightBorder: year + 1 < summaryGlobal.length,
            suffix: suffix
          });
        }
      }))]
    })
  });
};
var LocationTypeHeaderRow = function (_a) {
  var locationTypeKey = _a.locationTypeKey,
    showTypeSums = _a.showTypeSums,
    typeSums = _a.typeSums,
    decimal = _a.decimal,
    suffix = _a.suffix,
    extraColCount = _a.extraColCount,
    closed = _a.closed,
    toggleType = _a.toggleType,
    sharedPeriodProps = _a.sharedPeriodProps,
    border = _a.border,
    yearCount = _a.yearCount;
  var t = useTranslation('wizard').t;
  return _jsxs(StyledLocationTypeRow, {
    children: [_jsx(TableHierarchyCell, __assign({
      stickySizes: STICKIES,
      component: "th",
      align: "left",
      nestLevel: 1,
      sx: {
        py: 2
      }
    }, {
      children: _jsx(ExpandableRowLabel, __assign({
        expanded: !closed,
        onToggle: toggleType,
        bold: true
      }, {
        children: t("message.".concat(locationTypeKey))
      }))
    })), !!extraColCount && _jsx(ExtraColumnEmptyCells, {
      count: extraColCount
    }), _jsx(PeriodCells, __assign({}, sharedPeriodProps, {
      renderCell: function (periodIdx, year, month) {
        var _a;
        var sum = showTypeSums ? (_a = typeSums[locationTypeKey]) === null || _a === void 0 ? void 0 : _a[periodIdx] : null;
        return _jsx(TableCell, __assign({
          greyColor: true,
          sx: {
            textAlign: 'center'
          },
          highlightBorder: border(month, year)
        }, {
          children: showTypeSums && _jsx(Typography, __assign({
            fontWeight: "bold"
          }, {
            children: formatValueWithSuffix(sum, decimal, suffix)
          }))
        }));
      },
      renderTotalCell: function (year) {
        return _jsx(TableCell, {
          greyColor: true
        }, "total-".concat(year));
      },
      renderAverageCell: function (year) {
        var vals = times(sharedPeriodProps.chunkSize, function (month) {
          var _a, _b;
          var periodIdx = month + year * sharedPeriodProps.chunkSize;
          return periodIdx < sharedPeriodProps.periodLength ? (_b = (_a = typeSums[locationTypeKey]) === null || _a === void 0 ? void 0 : _a[periodIdx]) !== null && _b !== void 0 ? _b : null : null;
        }).filter(function (v) {
          return v !== null;
        });
        return _jsx(TableCell, __assign({
          greyColor: true,
          sx: {
            textAlign: 'center'
          },
          highlightBorder: year + 1 < yearCount
        }, {
          children: showTypeSums && vals.length > 0 && _jsx(Typography, __assign({
            fontWeight: "bold"
          }, {
            children: formatValueWithSuffix(round(mean(vals), decimal !== null && decimal !== void 0 ? decimal : 0), decimal, suffix)
          }))
        }));
      }
    }))]
  });
};
var CountryHeaderRow = function (_a) {
  var country = _a.country,
    showGroupHeader = _a.showGroupHeader,
    extraColCount = _a.extraColCount,
    closed = _a.closed,
    toggleCountry = _a.toggleCountry,
    sharedPeriodProps = _a.sharedPeriodProps,
    border = _a.border,
    yearCount = _a.yearCount;
  var label = _jsx(ExpandableRowLabel, __assign({
    expanded: !closed,
    onToggle: showGroupHeader ? toggleCountry : undefined,
    bold: true
  }, {
    children: country
  }));
  return _jsxs(StyledCountryRow, {
    children: [showGroupHeader ? _jsx(TableHierarchyCell, __assign({
      stickySizes: STICKIES,
      component: "th",
      align: "left",
      nestLevel: 2
    }, {
      children: label
    })) : _jsx(TableCell, __assign({
      stickySizes: STICKIES,
      component: "th",
      align: "left"
    }, {
      children: label
    })), !!extraColCount && _jsx(ExtraColumnEmptyCells, {
      count: extraColCount
    }), _jsx(PeriodCells, __assign({}, sharedPeriodProps, {
      renderCell: function (_periodIdx, year, month) {
        return _jsx(TableCell, {
          highlightBorder: border(month, year)
        });
      },
      renderAverageCell: function (year) {
        return _jsx(TableCell, {
          highlightBorder: year + 1 < yearCount
        });
      }
    }))]
  });
};
var LocationDataRow = function (_a) {
  var location = _a.location,
    locIdx = _a.locIdx,
    showGroupHeader = _a.showGroupHeader,
    name = _a.name,
    min = _a.min,
    max = _a.max,
    decimal = _a.decimal,
    suffix = _a.suffix,
    disabled = _a.disabled,
    extraColumns = _a.extraColumns,
    sharedPeriodProps = _a.sharedPeriodProps,
    border = _a.border,
    summary = _a.summary;
  return _jsxs(StyledSiteRow, {
    children: [showGroupHeader ? _jsx(TableHierarchyCell, __assign({
      stickySizes: STICKIES,
      align: "left",
      nestLevel: 3
    }, {
      children: _jsx(Typography, {
        children: location.planLocation.locationName
      })
    })) : _jsx(TableCell, __assign({
      stickySizes: STICKIES,
      align: "left"
    }, {
      children: _jsx(Typography, {
        children: location.planLocation.locationName
      })
    })), !!extraColumns.length && _jsx(ExtraColumnCells, {
      extraColumns: extraColumns,
      fieldPrefix: "queueLocations.".concat(locIdx, ".")
    }), _jsx(PeriodCells, __assign({}, sharedPeriodProps, {
      renderCell: function (periodIdx, year, month) {
        return _jsx(StyledInputTableCell, __assign({
          padding: "none",
          highlightBorder: border(month, year)
        }, {
          children: _jsx(FormField, __assign({
            disableHelperText: true,
            required: true,
            name: "queueLocations.".concat(locIdx, ".volumes.").concat(periodIdx, ".").concat(name)
          }, {
            children: _jsx(NativeNumberInput, {
              min: min,
              max: max,
              decimal: decimal,
              disabled: disabled,
              suffix: suffix
            })
          }))
        }));
      },
      renderTotalCell: function (year) {
        var _a, _b;
        return _jsx(SummaryCell, {
          padding: "none",
          greyColor: true,
          value: (_b = (_a = summary[locIdx]) === null || _a === void 0 ? void 0 : _a.chunks[year]) === null || _b === void 0 ? void 0 : _b.total,
          decimal: decimal,
          suffix: suffix
        });
      },
      renderAverageCell: function (year) {
        var _a, _b, _c;
        return _jsx(SummaryCell, {
          padding: "none",
          greyColor: true,
          value: (_b = (_a = summary[locIdx]) === null || _a === void 0 ? void 0 : _a.chunks[year]) === null || _b === void 0 ? void 0 : _b.avg,
          decimal: decimal,
          highlightBorder: year + 1 < ((_c = summary[locIdx]) === null || _c === void 0 ? void 0 : _c.chunks.length),
          suffix: suffix
        });
      }
    }))]
  }, location.id);
};
var LocationRows = function (_a) {
  var countryLocations = _a.countryLocations,
    showGroupHeader = _a.showGroupHeader,
    locIndexMap = _a.locIndexMap,
    rowProps = __rest(_a, ["countryLocations", "showGroupHeader", "locIndexMap"]);
  return _jsx(_Fragment, {
    children: countryLocations.map(function (location) {
      var locIdx = locIndexMap.get(location.id);
      if (locIdx === undefined) return null;
      return _jsx(LocationDataRow, __assign({
        location: location,
        locIdx: locIdx,
        showGroupHeader: showGroupHeader
      }, rowProps), location.id);
    })
  });
};
var CountryGroup = function (_a) {
  var country = _a.country,
    countryLocations = _a.countryLocations,
    showGroupHeader = _a.showGroupHeader,
    locIndexMap = _a.locIndexMap,
    extraColumns = _a.extraColumns,
    rowProps = __rest(_a, ["country", "countryLocations", "showGroupHeader", "locIndexMap", "extraColumns"]);
  var _b = React.useState(false),
    closed = _b[0],
    setClosed = _b[1];
  return _jsxs(React.Fragment, {
    children: [_jsx(CountryHeaderRow, __assign({
      country: country,
      showGroupHeader: showGroupHeader,
      extraColCount: extraColumns.length,
      closed: closed,
      toggleCountry: function () {
        return setClosed(function (p) {
          return !p;
        });
      }
    }, rowProps)), (!showGroupHeader || !closed) && _jsx(LocationRows, __assign({
      country: country,
      countryLocations: countryLocations,
      showGroupHeader: showGroupHeader,
      locIndexMap: locIndexMap,
      extraColumns: extraColumns
    }, rowProps))]
  });
};
var CountryRows = function (_a) {
  var countries = _a.countries,
    showGroupHeader = _a.showGroupHeader,
    locIndexMap = _a.locIndexMap,
    extraColumns = _a.extraColumns,
    rowProps = __rest(_a, ["countries", "showGroupHeader", "locIndexMap", "extraColumns"]);
  return _jsx(_Fragment, {
    children: Object.entries(countries).map(function (_a) {
      var country = _a[0],
        countryLocations = _a[1];
      return _jsx(CountryGroup, __assign({
        country: country,
        countryLocations: countryLocations,
        showGroupHeader: showGroupHeader,
        locIndexMap: locIndexMap,
        extraColumns: extraColumns
      }, rowProps), country);
    })
  });
};
var LocationTypeGroup = function (_a) {
  var locationTypeKey = _a.locationTypeKey,
    countries = _a.countries,
    showGroupHeader = _a.showGroupHeader,
    showTypeSums = _a.showTypeSums,
    locIndexMap = _a.locIndexMap,
    summary = _a.summary,
    typeSums = _a.typeSums,
    extraColumns = _a.extraColumns,
    rowProps = __rest(_a, ["locationTypeKey", "countries", "showGroupHeader", "showTypeSums", "locIndexMap", "summary", "typeSums", "extraColumns"]);
  var _b = React.useState(false),
    closed = _b[0],
    setClosed = _b[1];
  return _jsxs(React.Fragment, {
    children: [showGroupHeader && _jsx(LocationTypeHeaderRow, __assign({
      locationTypeKey: locationTypeKey,
      showTypeSums: showTypeSums,
      typeSums: typeSums,
      extraColCount: extraColumns.length,
      closed: closed,
      toggleType: function () {
        return setClosed(function (p) {
          return !p;
        });
      }
    }, rowProps)), (!showGroupHeader || !closed) && _jsx(CountryRows, __assign({
      countries: countries,
      showGroupHeader: showGroupHeader,
      locIndexMap: locIndexMap,
      summary: summary,
      extraColumns: extraColumns
    }, rowProps))]
  });
};
export var LocationTypeRows = function (_a) {
  var groupedLocations = _a.groupedLocations,
    showGroupHeader = _a.showGroupHeader,
    showTypeSums = _a.showTypeSums,
    locIndexMap = _a.locIndexMap,
    summary = _a.summary,
    typeSums = _a.typeSums,
    extraColumns = _a.extraColumns,
    rowProps = __rest(_a, ["groupedLocations", "showGroupHeader", "showTypeSums", "locIndexMap", "summary", "typeSums", "extraColumns"]);
  return _jsx(_Fragment, {
    children: Object.entries(groupedLocations).map(function (_a) {
      var locationType = _a[0],
        countries = _a[1];
      var locationTypeKey = locationType;
      return _jsx(LocationTypeGroup, __assign({
        locationTypeKey: locationTypeKey,
        countries: countries,
        showGroupHeader: showGroupHeader,
        showTypeSums: showTypeSums,
        locIndexMap: locIndexMap,
        summary: summary,
        typeSums: typeSums,
        extraColumns: extraColumns
      }, rowProps), locationTypeKey);
    })
  });
};
export var TypeSumsTotalRow = function (_a) {
  var planTypeCategory = _a.planTypeCategory,
    decimal = _a.decimal,
    suffix = _a.suffix,
    extraColCount = _a.extraColCount,
    grandTotals = _a.grandTotals,
    sharedPeriodProps = _a.sharedPeriodProps,
    border = _a.border,
    yearCount = _a.yearCount;
  var t = useTranslation('wizard').t;
  return _jsxs(StyledTotalRow, {
    children: [_jsx(TableCell, __assign({
      stickySizes: STICKIES,
      component: "th",
      align: "left"
    }, {
      children: _jsxs(Typography, __assign({
        fontWeight: "bold"
      }, {
        children: [planTypeCategory === PlanTypeCategory.ProdHours ? t('prodHours.totalProductionHours') : t('workVolume.totalVolumeAllocation'), suffix ? " ".concat(suffix) : '']
      }))
    })), !!extraColCount && _jsx(ExtraColumnEmptyCells, {
      count: extraColCount
    }), _jsx(PeriodCells, __assign({}, sharedPeriodProps, {
      renderCell: function (periodIdx, year, month) {
        return _jsx(TableCell, __assign({
          sx: {
            textAlign: 'center'
          },
          highlightBorder: border(month, year)
        }, {
          children: _jsx(Typography, __assign({
            fontWeight: "bold"
          }, {
            children: formatValueWithSuffix(grandTotals[periodIdx], decimal, suffix)
          }))
        }));
      },
      renderTotalCell: function (year) {
        return _jsx(TableCell, {}, "total-".concat(year));
      },
      renderAverageCell: function (year) {
        return _jsx(TableCell, {
          highlightBorder: year + 1 < yearCount
        });
      }
    }))]
  });
};